import { t } from "../../lib/i18n";
import React from 'react';
import { Link } from 'react-router-dom';

interface ArticleMetadata {
  title: string;
  slug: string;
  publish_date?: string;
  published_at?: string; // Alpha Brand schema
  featured_image?: string;
  thumbnail_url?: string; // Alpha Brand schema
}

interface ArticleCardProps {
  article: ArticleMetadata;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article }) => {
  // Support both schemas, prioritize Alpha Brand's professional thumbnails
  const creImages = [
    "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1200", // Glass skyscraper
    "https://images.unsplash.com/photo-1554469384-e58fac16e23a?q=80&w=1200", // Corporate building
    "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1200", // Office conference room
    "https://images.unsplash.com/photo-1497366754035-f200968a6e72?q=80&w=1200", // Sleek co-working space
    "https://images.unsplash.com/photo-1497215728101-856f4ea42174?q=80&w=1200", // Modern office desk
    "https://images.unsplash.com/photo-1577495508048-b635879837f1?q=80&w=1200", // Modern exterior
    "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?q=80&w=1200", // Premium corporate property
    "https://images.unsplash.com/photo-1556800045-813d10008efc?q=80&w=1200"  // Elegant commercial lobby
  ];
  
  // Consistent polynomial rolling hash for fallback image based on slug
  let hash = 0;
  for (let i = 0; i < article.slug.length; i++) {
    hash = (hash * 31 + article.slug.charCodeAt(i)) | 0;
  }
  const fallbackImage = creImages[Math.abs(hash) % creImages.length];
  
  let imageUrl = article.thumbnail_url || article.featured_image || fallbackImage;
  if (imageUrl && imageUrl.startsWith("https://storage.googleapis.com/") && imageUrl.includes("X-Goog-Algorithm=")) {
    imageUrl = imageUrl.split("?")[0];
  }
  
  // Support both date fields
  const displayDate = article.published_at || article.publish_date || new Date().toISOString();


  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden flex flex-col h-full">
      <div className="w-full overflow-hidden">
        <img 
          src={imageUrl} 
          alt={article.title} 
          className="w-full h-auto transition-transform duration-500 hover:scale-105"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = fallbackImage;
          }}
        />
      </div>
      <div className="p-6 flex-grow flex flex-col">
        <h2 className="text-xl font-semibold mb-2 text-gray-800 line-clamp-2">
          <Link to={`/blog/${article.slug}`} className="hover:text-blue-600">
            {article.title}
          </Link>
        </h2>
        <p className="text-gray-600 text-sm mb-4">
          {t("articleCard.k1")} {new Date(displayDate).toLocaleDateString()}
        </p>
        <div className="mt-auto">
          <Link 
            to={`/blog/${article.slug}`} 
            className="text-blue-700 hover:text-blue-900 hover:underline font-medium inline-flex items-center"
            aria-label={`Read more about ${article.title}`}
          >
            {t("articleCard.k2")} <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ArticleCard;
